import { useEffect, useState } from 'react';
import type { HelpField } from './model';
import { findHelpTarget, useHelpHighlight } from './useHelpHighlight';

/**
 * Which of `ids` currently have a `[data-help-id]` in the document. Re-checked (one rAF per
 * burst) whenever the DOM changes, so a field on a tab that just opened turns live.
 */
function useMountedIds(ids: string[]): Set<string> {
  const key = ids.join('\u0000');
  const [mounted, setMounted] = useState<Set<string>>(
    () => new Set(ids.filter((id) => findHelpTarget(id) != null)),
  );
  useEffect(() => {
    const list = key ? key.split('\u0000') : [];
    let frame = 0;
    const check = () => {
      frame = 0;
      const next = new Set(list.filter((id) => findHelpTarget(id) != null));
      setMounted((prev) =>
        prev.size === next.size && [...next].every((id) => prev.has(id)) ? prev : next,
      );
    };
    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(check);
    };
    schedule();
    const obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-help-id'] });
    return () => {
      obs.disconnect();
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [key]);
  return mounted;
}

/**
 * The "Fields" list under the fields section (ADR-0066): one row per `data-help-id` in the
 * compiled `fields` map. Hover highlights the control; a row whose control is not on the screen
 * right now (another tab, a hidden pane) is greyed.
 */
export function HelpFields({ fields }: { fields: Record<string, HelpField> }) {
  const { highlight, clear } = useHelpHighlight();
  const entries = Object.entries(fields);
  const mounted = useMountedIds(entries.map(([id]) => id));
  if (entries.length === 0) return null;
  return (
    <dl className="mt-2 divide-y divide-slate-100 rounded border border-slate-200 text-xs" data-testid="help-fields">
      {entries.map(([id, f]) => {
        const live = mounted.has(id);
        return (
          <div
            key={id}
            data-field-id={id}
            onMouseEnter={() => highlight(id)}
            onMouseLeave={clear}
            title={live ? undefined : 'Not on this screen right now'}
            className={'grid grid-cols-[minmax(6rem,35%)_1fr] gap-2 px-2 py-1 ' + (live ? 'hover:bg-phblue-50' : 'text-slate-400')}
          >
            <dt className={'font-semibold ' + (live ? 'text-slate-800' : 'text-slate-400')}>{f.label}</dt>
            <dd className={live ? 'text-slate-700' : 'text-slate-400'}>{f.help}</dd>
          </div>
        );
      })}
    </dl>
  );
}
